import { unwrap } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { toast } from "@sagot/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { queryKeys } from "../../../lib/query-keys";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { datedFilename, saveBlob } from "../shared/download";
import type { GapIgnoreReason, GapView } from "./gap-model";
import type { AssignableMember } from "./gap-row-menu";
import { useAssignGap, useBulkGaps, useExportGaps, useIgnoreGap } from "./use-gaps";

const MEMBERS_LIMIT = 200;

/** Active members for "Assign to" (same cache entry as the Team page; only with team.view). */
export function useAssignableMembers(enabled: boolean): AssignableMember[] | null {
  const api = useApi();
  const wsId = useWorkspaceId();
  const members = useQuery({
    queryKey: queryKeys.members(wsId),
    enabled,
    queryFn: async () => (await unwrap(api.GET("/v1/members", { params: { query: { limit: MEMBERS_LIMIT } } }))).items,
  });
  if (!enabled || !members.data) return null;
  return members.data.filter((m) => m.status === "active").map((m) => ({ userId: m.user_id, name: m.name }));
}

/** Target of the ignore dialog: one row or the current selection. */
export type IgnoreTarget = { kind: "one"; gap: GapView } | { kind: "bulk"; ids: string[] } | null;

/** Assign / ignore / bulk / export mutations with their toasts. */
export function useGapActions(clearSelection: () => void) {
  const { t } = useTranslation();
  const errorMessage = useErrorMessage();
  const assign = useAssignGap();
  const ignore = useIgnoreGap();
  const bulk = useBulkGaps();
  const exportGaps = useExportGaps();
  const [ignoreTarget, setIgnoreTarget] = useState<IgnoreTarget>(null);
  const onError = (error: unknown) => toast.error(errorMessage(error));

  const assignOne = (gap: GapView, member: AssignableMember | null) => {
    assign.mutate(
      { gapId: gap.id, userId: member?.userId ?? null },
      { onSuccess: () => toast.success(member ? t("engage.gaps.assigned", { name: member.name }) : t("engage.gaps.unassigned")), onError },
    );
  };

  const assignMany = (ids: string[], member: AssignableMember) => {
    bulk.mutate(
      { action: "assign", ids, assignee_user_id: member.userId },
      {
        onSuccess: (result) => {
          toast.success(t("engage.gaps.bulk.assigned", { count: result.updated, name: member.name }));
          clearSelection();
        },
        onError,
      },
    );
  };

  const confirmIgnore = (reason: GapIgnoreReason) => {
    const target = ignoreTarget;
    if (!target) return;
    const done = () => {
      setIgnoreTarget(null);
      clearSelection();
    };
    if (target.kind === "one") {
      ignore.mutate({ gapId: target.gap.id, reason }, { onSuccess: () => { toast.info(t("engage.gaps.ignore.done")); done(); }, onError });
      return;
    }
    bulk.mutate(
      { action: "ignore", ids: target.ids, ignore_reason: reason },
      { onSuccess: (result) => { toast.info(t("engage.gaps.bulk.ignored", { count: result.updated })); done(); }, onError },
    );
  };

  const download = (agentId: string | null) => {
    exportGaps.mutate(agentId, {
      onSuccess: (blob) => {
        const file = datedFilename("knowledge-gaps", "csv");
        saveBlob(blob, file);
        toast.success(t("engage.gaps.exported", { file }));
      },
      onError: () => toast.error(t("engage.exportFailed")),
    });
  };

  return {
    assignOne,
    assignMany,
    ignoreTarget,
    setIgnoreTarget,
    confirmIgnore,
    ignorePending: ignore.isPending || bulk.isPending,
    bulkPending: bulk.isPending,
    download,
    exporting: exportGaps.isPending,
  };
}
